// Task Details Page Component
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { toast } from 'react-toastify';
import moment from 'utils/moment';
import Permissions from 'react-redux-permissions';

// MUI v4 Imports
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Box
} from '@material-ui/core';

// Existing custom components & actions
import { renderInfoForType } from 'utils/helpers';
import Separator from 'components/separator';
import Select from '../../../components/Select/Select';
import { fetchTaskDetails, updateTaskOccurrence, toggleCompleteTask, resetCompleteTask } from '../redux/actions';

import icon from '../../../icons/Tasks.png';
import SectionHeader from '../../../components/sectionHeader';
import HeaderBack from '../../../components/headerBack';
import FormattedMessageWithClass from '../../../components/formattedMessageWithClass/FormattedMessageWithClass';
import Loading from '../../../components/loading';
import Clickable from '../../../components/clickable/Clickable';

const StatusDropdown = lazy(() => import(/* webpackChunkName: "StatusDropdown" */ './StatusDropdown/StatusDropdown'));
const History = lazy(() => import(/* webpackChunkName: "History" */ '../../../components/activityLog/History'));
const Modal = lazy(() => import(/* webpackChunkName: "Modal" */ '../../../components/modal'));
const FileSlider = lazy(() => import(/* webpackChunkName: "FileSlider" */ '../../../components/photoCarousel/FileSlider'));
const CompleteTaskModal = lazy(() => import(/* webpackChunkName: "CompleteTaskModal" */ './CompleteTaskModal'));

// Local helpers requested by user code but not found in project
const getConvertToLinks = (text) => {
  if (!text) return '';
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(
    urlRegex,
    url => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
};

// Custom MUI v4 Styles for Dashboard Look
const useStyles = makeStyles(theme => ({
  pageContainer: {
    backgroundColor: '#f4f5f7',
    paddingBottom: '32px',
    height: 'stretch',
    minHeight: 'fit-content'
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff'
  },
  card: {
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    borderRadius: '8px'
  },
  cardContent: {
    padding: '16px 20px !important'
  },
  cardHeader: {
    fontFamily: '"Lato", sans-serif',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    color: '#242e42',
    fontSize: '16px',
    letterSpacing: '0.11px'
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: '"Lato", sans-serif',
    fontWeight: 'bold',
    fontSize: '16px',
    letterSpacing: '0.11px',
    color: '#242e42',
    marginBottom: theme.spacing(2)
  },
  labelText: {
    color: '#8C9EA7',
    fontFamily: '"Lato", sans-serif',
    fontSize: '13px',
    letterSpacing: '1.86px',
    lineHeight: '16px',
    textTransform: 'uppercase',
    marginBottom: '4px'
  },
  valueText: {
    fontFamily: '"Lato", sans-serif',
    fontSize: '15px',
    letterSpacing: '0.1px',
    lineHeight: '25px',
    color: '#242E42',
    display: 'flex',
    alignItems: 'center'
  },
  activityRow: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '2rem',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& img': {
      width: '40px',
      height: '40px',
      marginRight: '12px'
    }
  },
  arrow: {
    width: '14px',
    height: '14px',
    display: 'flex',
    alignItems: 'center'
  },
  svg: {
    width: '100%',
    height: '100%',
    stroke: '#3A61A8'
  },
  quickLink: {
    display: 'block',
    marginBottom: theme.spacing(1),
    color: '#0052CC',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  linkLable: {
    marginRight: '3px'
  },
  badge: {
    display: 'inline-block',
    color: 'grey',
    fontSize: 'medium',
    fontWeight: '600'
  },
  attachmentsContainer: {
    display: 'flex',
    height: '125px',
    margin: '10px 0',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
    transform: 'translateZ(0)',
    gap: '12px',
    paddingBottom: '10px',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      height: '6px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#E6EAEE',
      borderRadius: '3px'
    }
  },
  slimScroll: {
    scrollbarWidth: 'thin',
    scrollbarColor: '#B0B9BD transparent',
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#B0B9BD',
      borderRadius: '10px'
    }
  },
  attachmentWrapper: {
    position: 'relative',
    flex: '0 0 200px',
    width: '200px',
    minWidth: '200px',
    height: '100%',
    '& img': {
      height: '100%',
      width: '100%',
      borderRadius: '4px',
      padding: '5px',
      objectFit: 'cover',
      border: '1px solid #E6EAEE',
      display: 'block',
      cursor: 'pointer'
    },
    '& p': {
      height: '100%',
      width: '100%',
      minWidth: '200px',
      borderRadius: '4px',
      padding: '10px',
      color: '#007bff',
      backgroundColor: '#f4f7fd',
      border: '1px solid #E6EAEE',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      wordBreak: 'break-all',
      margin: 0,
      fontSize: '14px',
      cursor: 'pointer'
    }
  },
  backCustomStyle: {
    lineHeight: '0px'
  },
  // Local overrides for the Select component used as a view switcher in the task header.
  viewDiv: {
    position: 'relative',
    '& > div': {
      position: 'relative'
    },
    // Compact the trigger row
    '& [class*="selected"]': {
      height: '36px !important',
      lineHeight: '36px !important',
      fontSize: '18px !important',
      padding: '0 28px 0 10px !important',
      display: 'inline-flex !important',
      alignItems: 'center !important',
      // arrow position
      '&:after': {
        right: '8px !important',
        top: '50% !important',
        marginTop: '-4px !important'
      }
    },
    // Make the dropdown overlay
    '& [class*="dropdown"]': {
      position: 'absolute !important',
      top: 'calc(100% + 2px) !important',
      right: '0 !important',
      left: 'auto !important',
      minWidth: '120px !important',
      width: 'auto !important',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12) !important',
      borderRadius: '6px !important'
    },
    '& [class*="option"]': {
      padding: '10px 20px !important',
      whiteSpace: 'nowrap !important'
    }
  }
}));

const TaskDetailPage = ({ task: taskProp, section, intl }) => {
  const classes = useStyles();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const startDateFromQuery = queryParams.get('start_date');
  const startDateFromState = location.state?.start_date;
  const start_date = startDateFromState ?? startDateFromQuery;
  const dispatch = useDispatch();

  // Redux Selectors
  const { taskDetails, updateTaskAction, completeTask } = useSelector(
    state => state.tasks
  ) || {};

  // Local State
  const [task, setTask] = useState(taskProp);
  const [generalTaskStatus, setGeneralTaskStatus] = useState('0');
  const [activeTab, setActiveTab] = useState('details');
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const openAttachmentPreview = (idx) => {
    setInitialSlide(idx);
    setIsAttachmentModalOpen(true);
  };

  // Fetch task details on mount / when id changes (always refetch)
  useEffect(() => {
    if (!id || !start_date) return;
    setTask(null);
    dispatch(fetchTaskDetails(id, start_date));
  }, [id, start_date, dispatch]);

  useEffect(() => {
    if (taskDetails?.data) {
      setTask(taskDetails.data);
    }
    if (taskDetails?.error) {
      toast.error(
        taskDetails.error?.message
        || intl.formatMessage({
            id: 'tasks.details.fetch_failed',
            defaultMessage: 'Failed to fetch task details'
          })
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskDetails]);

  useEffect(() => {
    if (completeTask?.success) {
      toast.success(intl.formatMessage({ id: 'tasks.toast.completed_success', defaultMessage: 'Task marked as completed' }));
      setIsCompleteModalOpen(false);
      dispatch(fetchTaskDetails(id, start_date));
      dispatch(resetCompleteTask());
    }
    if (completeTask?.error) {
      toast.error(
        completeTask.error?.message
          || intl.formatMessage({
            id: 'tasks.toast.complete_failed',
            defaultMessage: 'Failed to complete task'
          })
      );
      dispatch(resetCompleteTask());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeTask, dispatch, id, start_date]);

  useEffect(() => {
    if (isUpdatingStatus && updateTaskAction?.success) {
      if (id && start_date) {
        dispatch(fetchTaskDetails(id, start_date));
      }
      setIsUpdatingStatus(false);
    } else if (isUpdatingStatus && updateTaskAction?.error) {
      setIsUpdatingStatus(false);
    }
  }, [updateTaskAction, isUpdatingStatus, id, start_date, dispatch]);

  // --- Core Logic & Helpers (Extracted from Class) ---
  const getGeneralTaskStatus = React.useCallback((currentTask) => {
    const { task_occurrences, completed: taskCompleted } = currentTask || {};
    const { completed: occurrenceCompleted, status: occurrenceStatus } = task_occurrences || {};

    if (
      ['completed_tasks'].includes(section)
      || occurrenceCompleted === true
      || taskCompleted === true
    ) {
      return '3';
    }
    const rawStatus = occurrenceStatus
      ?? currentTask?.status
      ?? currentTask?.event?.status;

    if ([null, ''].includes(rawStatus)) {
      return occurrenceCompleted ? '3' : '0';
    }

    if (typeof rawStatus === 'number' && rawStatus >= 0 && rawStatus <= 3) return String(rawStatus);

    const normalized = String(rawStatus)
      .toLowerCase()
      .replace(/\s+/g, '_');
    if (['0', '1', '2', '3'].includes(normalized)) return normalized;
    if (normalized.includes('acknowledged')) return '1';
    if (normalized.includes('progress')) return '2';
    if (normalized.includes('complete')) return '3';
    return '0';
  }, [section]);

  useEffect(() => {
    if (task) {
      setGeneralTaskStatus(getGeneralTaskStatus(task));
    }
  }, [getGeneralTaskStatus, task]);

  const updateGeneralTaskStatusStr = (statusId) => {
    const taskDate = task?.selected_occurrence_date
      || task?.end
      || task?.event?.end_date
      || new Date().toISOString();
    const taskTitle = task?.task_occurrences?.title
      || task?.title
      || task?.event?.title
      || task?.event?.name
      || '';
    const taskEventId = task.event?.id || task.id;
    if (!taskEventId || !taskDate || Number.isNaN(Number(statusId))) return;

    const data = new FormData();
    data.append('date', taskDate);
    data.append('status', String(statusId));
    data.append('name', taskTitle);

    setIsUpdatingStatus(true);
    dispatch(updateTaskOccurrence(taskEventId, data));
  };

  const handleStatusUpdate = (item) => {
    const status = String(item?.id ?? '');
    if (!status) return;
    if (status === '3') {
      setIsCompleteModalOpen(true);
      return;
    }
    setGeneralTaskStatus(status);
    updateGeneralTaskStatusStr(status);
  };

  const handleConfirmComplete = () => {
    if (task && !completeTask?.loading) {
      dispatch(resetCompleteTask());
      dispatch(toggleCompleteTask(task, '', moment().format()));
    }
  };

  const handleCancelComplete = () => {
    setIsCompleteModalOpen(false);
  };

  const fetchTaskAssignedUser = () => {
  const { assigned_users = [], assigned_roles = [], assigned_portal_users = [] } = task ?? {};

  const hasUsers = assigned_users.length > 0;
  const hasPUsers = assigned_portal_users.length > 0;
  const hasRoles = assigned_roles.length > 0;

  // Both users and roles assigned → always multiple
    if (hasUsers || hasRoles) return <FormattedMessage id="tasks.details.multiple_users" defaultMessage="Multiple Users" />;

    if (hasUsers) {
      return assigned_users.length === 1
        ? assigned_users[0].fullname
        : <FormattedMessage id="tasks.details.multiple_users" defaultMessage="Multiple Users" />;
    }

    if (hasRoles) {
      return assigned_roles.length === 1
        ? assigned_roles[0].name
        : <FormattedMessage id="tasks.details.multiple_users" defaultMessage="Multiple Users" />;
    }
    if (hasPUsers) {
      return assigned_portal_users.length === 1
        ? assigned_portal_users[0].fullname
        : <FormattedMessage id="tasks.details.multiple_users" defaultMessage="Multiple Users" />;
    }

    return '-';
  };

  const fetchTaskAssignedPosition = () => {
    const { assigned_positions = [] } = task ?? {};

    if (assigned_positions.length > 0) {
      return assigned_positions.length === 1
        ? assigned_positions[0].name
        : <FormattedMessage id="tasks.details.multiple_positions" defaultMessage="Multiple Positions" />;
    }

    return '-';
  };

  const taskEventId = task?.event?.id || task?.id;
  const isStaleData = task && String(taskEventId) !== String(id);
  const isLoading = (taskDetails?.loading && !task) || isStaleData;
  if (isLoading) return <Loading loadingStatus />;

  if (!task || isStaleData) return null;

  const getDescriptionValue = () => {
    if (typeof task.description === 'string' && task.description.trim().length > 0) {
      return task.description;
    }
    if (typeof task.event?.description === 'string' && task.event.description.trim().length > 0) {
      return task.event.description;
    }
    return task.title || task.event?.title || '';
  };

  const descriptionValue = getDescriptionValue();

  const isCompletedSection = section === 'completed_tasks';
  const isTaskCompleted = generalTaskStatus === '3';
  const isStatusDropdownDisabled = !!updateTaskAction?.loading || isCompletedSection || isTaskCompleted;


  const selectedView = {
    key: activeTab,
    name: ['details'].includes(activeTab) ? (
      intl.formatMessage({ id: 'tasks.details.details', defaultMessage: 'Details' })
    ) : (
      intl.formatMessage({ id: 'tasks.details.activity', defaultMessage: 'Activity' })
    )
  };

  const changeView = (val) => {
    setActiveTab(val.key);
  };


  return (
    <div className={classes.pageContainer}>
      <Box
        style={{ backgroundColor: '#fafbfd', borderBottom: '1px solid #E6EAEE' }}
      >
        <Grid container alignItems="center" style={{ paddingRight: '5px', backgroundColor: '#fafbfd' }}>
          {/* LEFT — 60% — Section Header */}
          <Grid
            item
            style={{
              flex: '0 0 60%',
              maxWidth: '60%'
            }}
          >
            <SectionHeader
              icon={icon}
              translationID="invalid"
              defaultTitle={task.title || task.event?.title}
            />
          </Grid>

          {/* RIGHT — 40% — Actions */}
          <Grid
            item
            style={{
              flex: '0 0 39%',
              maxWidth: '39%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            {/* View Switcher */}
            <div className={classes.viewDiv}>
              <Select
                value={selectedView}
                options={[
                  {
                    key: 'details',
                    name: intl.formatMessage({ id: 'tasks.details.details', defaultMessage: 'Details' })
                  },
                  {
                    key: 'activities',
                    name: intl.formatMessage({ id: 'tasks.details.activity', defaultMessage: 'Activity' })
                  }
                ].filter(i => selectedView.key !== i.key)}
                onChange={changeView}
              />
            </div>
            <Separator />
            <Permissions allowed={[]}>
              <div
                style={{
                  pointerEvents: isStatusDropdownDisabled ? 'none' : 'auto',
                  opacity: isStatusDropdownDisabled ? 0.6 : 1,
                  marginTop: '2px'
                }}
              >
                <Suspense fallback={<Loading />}>
                  <StatusDropdown
                    value={generalTaskStatus}
                    hideArrow={['3'].includes(generalTaskStatus)}
                    isCompleted={isTaskCompleted}
                    items={[
                      { id: '0', title: intl.formatMessage({ id: 'tasks.status.new', defaultMessage: 'New' }), icon: true },
                      { id: '1', title: intl.formatMessage({ id: 'tasks.status.acknowledged', defaultMessage: 'Acknowledged' }), icon: true },
                      { id: '2', title: intl.formatMessage({ id: 'tasks.status.inProgress', defaultMessage: 'In Progress' }), icon: true },
                      { id: '3', title: intl.formatMessage({ id: 'tasks.status.completed', defaultMessage: 'Completed' }), icon: true }
                    ]}
                    onChange={item => handleStatusUpdate(item)}
                  />
                </Suspense>
              </div>
            </Permissions>

          </Grid>
        </Grid>
      </Box>
      <HeaderBack
        translationID="inspections.new.prev"
        translationDefault="Back"
        backRoute={`${isTaskCompleted ? '/completed_tasks' : '/my_tasks'}`}
        className={classes.backCustomStyle}
      />

      {/* --- Main Grid --- */}
      <div style={{ padding: '3rem 6rem' }}>
        <Grid container spacing={4}>
          {/* LEFT SIDEBAR (~20%) */}
          <Grid item xs={12} md={3}>
            <Card className={classes.card}>
              <CardContent style={{ padding: 0 }}>
                <div
                  style={{ padding: '16px', borderBottom: '1px solid #E6EAEE' }}
                >
                  <Typography
                    className={classes.cardHeader}
                    style={{ marginBottom: 0 }}
                  >
                    <FormattedMessageWithClass
                      id="tasks.details.overview"
                      defaultMessage="Task Overview"
                    />
                  </Typography>
                </div>

                {/* Label Row */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '16px',
                    borderBottom: '1px solid #E6EAEE',
                    alignItems: 'flex-end'
                  }}
                >
                  <Typography
                    className={classes.labelText}
                    style={{ marginBottom: 0 }}
                  >
                    <FormattedMessageWithClass
                      id="tasks.details.label"
                      defaultMessage="LABEL"
                    />
                  </Typography>
                  <div className={classes.badge}>
                    {task.label || task.event?.label ? (
                      <FormattedMessageWithClass
                        id={`todo.label.${task.label || task.event?.label}`}
                        defaultMessage={task.label || task.event?.label}
                      />
                    ) : (
                      <FormattedMessageWithClass
                        id="todo.label.generalTask"
                        defaultMessage="General Task"
                      />
                    )}
                  </div>
                </div>

                {/* Due Date Row */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '16px',
                    borderBottom: '1px solid #E6EAEE',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    className={classes.labelText}
                    style={{ marginBottom: 0 }}
                  >
                    <FormattedMessageWithClass
                      id="tasks.details.dueDate"
                      defaultMessage="DUE DATE"
                    />
                  </Typography>
                  <Typography
                    variant="body2"
                    className={classes.valueText}
                    style={{ fontWeight: 400, color: '#242e42' }}
                  >
                    {(() => {
                      const dDate = task.due_date || task.event?.due_date;
                      const dTime = task.due_time || task.event?.due_time;
                      if (dDate && dTime) return renderInfoForType({ type: 'datetime' }, `${dDate} ${dTime}`);
                      if (dDate) return renderInfoForType({ type: 'date' }, dDate);
                      if (dTime) return renderInfoForType({ type: 'time' }, dTime);
                      return '-';
                    })()}
                  </Typography>
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* MAIN CONTENT (~80%) */}
          <Grid item xs={12} md={9}>
            {/* Task Details Card */}
            {activeTab === 'details' && (
              <Card className={classes.card}>
                <div
                    style={{ padding: '16px', borderBottom: '1px solid #E6EAEE', marginBottom: '20px' }}
                  >
                  <Typography className={classes.cardHeader}>
                    <FormattedMessageWithClass
                      id="tasks.details.taskDetails"
                      defaultMessage="Task Details"
                    />
                  </Typography>
                </div>
                <CardContent className={classes.cardContent}>

                  <Grid container spacing={4}>
                    {/* Row 1 */}
                    <Grid item xs={6}>
                      <Typography className={classes.labelText}>
                        <FormattedMessageWithClass
                          id="tasks.details.assignedTo"
                          defaultMessage="Assigned To"
                        />
                      </Typography>
                      <Typography className={classes.valueText}>
                        {fetchTaskAssignedUser()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography className={classes.labelText}>
                        <FormattedMessageWithClass
                          id="tasks.details.assignedPosition"
                          defaultMessage="Assigned Position"
                        />
                      </Typography>
                      <Typography className={classes.valueText}>
                        {fetchTaskAssignedPosition()}
                      </Typography>
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={6}>
                      <Typography className={classes.labelText}>
                        <FormattedMessageWithClass
                          id="tasks.details.shift"
                          defaultMessage="Shift"
                        />
                      </Typography>
                      <Typography className={classes.valueText}>
                        {task.shift_name || task.event?.shift_name || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography className={classes.labelText}>
                        <FormattedMessageWithClass
                          id="tasks.details.sourceType"
                          defaultMessage="Source Type"
                        />
                      </Typography>
                      <Typography className={classes.valueText}>
                        {task.event?.hazard_data || '-'}
                      </Typography>
                    </Grid>

                    {/* Row 3 */}
                    <Grid item xs={6}>
                      <Typography className={classes.labelText}>
                        <FormattedMessageWithClass
                          id="tasks.details.startDate"
                          defaultMessage="Start Date"
                        />
                      </Typography>
                      <Typography className={classes.valueText}>
                        {task.start_date || task.event?.start_date
                          ? renderInfoForType({ type: 'date' }, task.start_date || task.event.start_date)
                          : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography className={classes.labelText}>
                        <FormattedMessageWithClass
                          id="tasks.details.endDate"
                          defaultMessage="End Date"
                        />
                      </Typography>
                      <Typography className={classes.valueText}>
                        {task.end_date || task.event?.end_date
                          ? renderInfoForType({ type: 'date' }, task.end_date || task.event?.end_date)
                          : '-'}
                      </Typography>
                    </Grid>

                    {/* Row 4: Frequency (Rule) */}
                    {(task.rule || task.event?.rule) && (
                      <Grid item xs={12}>
                        <Typography className={classes.labelText}>
                          <FormattedMessageWithClass
                            id="tasks.details.frequency"
                            defaultMessage="Frequency"
                          />
                        </Typography>
                        <Typography className={classes.valueText}>
                          {(task.rule?.name || task.event?.rule?.name || '-')}
                          { (task.rule?.description || task.event?.rule?.description)
                            && ` (${task.rule?.description || task.event?.rule?.description})`
                          }
                        </Typography>
                      </Grid>
                    )}

                    {/* Row 5: Description */}
                    <Grid item xs={12}>
                      <Typography
                        className={classes.labelText}
                        style={{ marginTop: '8px' }}
                      >
                        <FormattedMessageWithClass
                          id="tasks.details.description"
                          defaultMessage="Description"
                        />
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: getConvertToLinks(descriptionValue)
                        }}
                        style={{
                          color: '#242E42',
                          fontFamily: '"Lato", sans-serif',
                          fontSize: '15px'
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Attachments Card */}
            {activeTab === 'details' && (task.attachments?.length > 0 || task.event?.attachments?.length > 0) && (
              <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.cardHeader}>
                    <FormattedMessageWithClass
                      id="tasks.details.attachments"
                      defaultMessage="Attachments"
                    />
                  </Typography>
                  <Divider
                    style={{
                      margin: '16px -20px 24px -20px',
                      backgroundColor: '#E4E7ED'
                    }}
                  />
                  <div className={classes.attachmentsContainer}>
                    {(task.attachments || task.event?.attachments || []).map((attachment, idx) => (
                      <div key={attachment.id} className={classes.attachmentWrapper}>
                         <Clickable onClick={() => openAttachmentPreview(idx)} style={{ width: '100%', height: '100%' }}>
                            {(attachment.type || attachment.content_type || '').includes('image') ? (
                               <img
                                 src={attachment.image}
                                 alt={attachment.name}
                               />
                            ) : (
                               <p>{attachment.name}</p>
                            )}
                         </Clickable>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Activity Card */}
            {activeTab === 'activities' && (
              <Card className={classes.card} style={{ maxHeight: 'calc(100vh - 18rem)' }}>
                <CardContent
                  className={classes.cardContent}
                  style={{ padding: 0 }}
                >
                  <Typography className={classes.cardHeader}>
                    <FormattedMessageWithClass
                      id="tasks.details.activity"
                      defaultMessage="Activity"
                    />
                  </Typography>
                  <Divider
                    style={{
                      margin: '16px 0 0 0',
                      backgroundColor: '#E4E7ED'
                    }}
                  />
                  <div
                    style={{
                      overflowY: 'auto',
                      maxHeight: 'calc(100vh - 23rem)'
                    }}
                    className={classes.slimScroll}
                  >
                    <Suspense fallback={<Loading />}>
                      <History logs={task?.history || []} loading={taskDetails?.loading} />
                    </Suspense>
                  </div>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </div>

      <Suspense fallback={<Loading />}>
        {isAttachmentModalOpen && (
          <Modal
            showIn={isAttachmentModalOpen}
            onClose={() => setIsAttachmentModalOpen(false)}
            closeIcon
            width="80%"
            minHeight="80%"
            contentStyles={{
              overflow: 'visible',
              background: '#E6EAEE',
              padding: '20px 40px'
            }}
          >
            <div style={{ padding: '20px' }}>
              <FileSlider
                photos={(task.attachments || task.event?.attachments || []).map(att => ({
                  ...att,
                  url: att.image,
                  id: att.id
                }))}
                initialSlide={initialSlide}
              />
            </div>
          </Modal>
        )}
      </Suspense>

      <Suspense fallback={<Loading />}>
        {isCompleteModalOpen && (
          <CompleteTaskModal
            showIn={isCompleteModalOpen}
            onClose={handleCancelComplete}
            onConfirm={handleConfirmComplete}
            taskName={task?.title || task?.event?.title}
            confirmDisabled={completeTask?.loading}
          />
        )}
      </Suspense>
    </div>
  );
};

export default injectIntl(TaskDetailPage);
